<template>
  <Disclaimer v-if="data.status === 'PUBLISHED'" />
  <div v-if="info" class="certificate">

    <h1 class="certificate__title">{{ $t('certificateComponent.CREATE_COPY_OF_MASTER_COURSE') }}</h1>
    <h3 class="certificate__step">{{ $t('certificateComponent.STEP_NUMBER', { number: 4 }) }}</h3>
    <h5 class="certificate__step-data">{{ $t('certificateComponent.CERTIFICATION') }}</h5>
    <EmptyState icon="error" :title="$t('certificateComponent.ON_DEMAND_COURSES_NO_CERTIFICATION')" v-if="data.courseType == 1" />
    <div class="certificate__conditions" v-else>
      <span class="certificate__conditions-title">{{ $t('certificateComponent.CONDITIONS_TO_CERTIFY') }}</span>
      <label class="certificate__conditions-check" for="check-1">
        <span>{{ $t('certificateComponent.PERCENTAGE_OF_MANDATORY_ACTIVITIES_SUBMITTED') }}</span>
      </label>
      <div class="certificate__conditions-percentage"> 
        <Input 
          type="number"
          id="percentaje"
          minNumber="0"
          maxNumber="100"
          :label="$t('certificateComponent.PERCENTAGE')"
          labelNextToNumber="%"
          v-model="info.percentajeToCertificate"
          :disabled="data.status === 'PUBLISHED'"
          :style="data.status === 'PUBLISHED' ? 'cursor: none; pointer-events: none; opacity: 0.5;' : ''"
        />
      </div>

      <label class="certificate__conditions-check" for="check-2" 
        :style="(!info.finalPractice || data.status === 'PUBLISHED') ? 'opacity:.5' : ''">
        <input v-model="info.needFinalPractice" 
          :disabled="!info.finalPractice || data.status === 'PUBLISHED'"
          :style="(!info.finalPractice || data.status === 'PUBLISHED') ? 'cursor:default' : ''"
          type="checkbox" id="check-2" name="check-2" 
        />
        <span>{{ $t('certificateComponent.PRACTICE_WITH_YOUR_STUDENTS') }}</span>
      </label>

      <Input 
        :label="$t('certificateComponent.PROGRAM_NAME')"
        id="certificateName"
        v-model="info.certificateName"
        :isError="info.certificateName === ''"
        :errorMessage="$t('certificateComponent.THIS_FIELD_IS_REQUIRED')"
        :disabled="data.status === 'PUBLISHED'"
        :style="data.status === 'PUBLISHED' ? 'cursor: none; pointer-events: none; opacity: 0.5;' : ''"
      />

      <Input 
        :label="$t('certificateComponent.SUBTITLE')"
        id="certificateDescription"
        v-model="info.certificateDescription"
        :isError="info.certificateDescription === ''"
        :errorMessage="$t('certificateComponent.THIS_FIELD_IS_REQUIRED')"
        :disabled="data.status === 'PUBLISHED'"
        :style="data.status === 'PUBLISHED' ? 'cursor: none; pointer-events: none; opacity: 0.5;' : ''"
      />

      <Select
          mode="simple" 
          id="modality" 
          :label="$t('certificateComponent.HOUR_TYPE')"
          :values="creationOptions.certificateHourTypes"
          valueLabel="name"
          valueProp="id" 
          v-model="info.certificateHourTypeId"
          :isError="info.certificateHourTypeId === ''"
          :errorMessage="$t('certificateComponent.THIS_FIELD_IS_REQUIRED')"
          :disabled="data.status === 'PUBLISHED'"
          :style="data.status === 'PUBLISHED' ? 'cursor: none; pointer-events: none; opacity: 0.5;' : ''"
          @input="info.certificateHourType = info.certificateHourType || {}; info.certificateHourType.id = info.certificateHourTypeId; info.certificateHourType.name = creationOptions.certificateHourTypes.find(certificateHourType => certificateHourType.id === info.certificateHourTypeId).name"
        />

      <Input 
        type="number"
        :label="$t('certificateComponent.DURATION_IN_HOURS', { hourType: getHourTypeName(info.certificateHourTypeId) })"
        id="durationInHours"
        v-model="info.durationInHours"
        :isError="info.durationInHours === ''"
        :errorMessage="$t('certificateComponent.THIS_FIELD_IS_REQUIRED')"
        :labelNextToNumber="$t('certificateComponent.HOURS')"
        :disabled="data.status === 'PUBLISHED'"
        :style="data.status === 'PUBLISHED' ? 'cursor: none; pointer-events: none; opacity: 0.5;' : ''"
      />

    </div>

    <div class="buttons">
      <button @click="router.push('structure')" class="button__backward"><i class="material-icons-outlined">arrow_back</i> {{ $t('global.BACK') }}</button>
      <button :disabled="submitting" @click="submit" class="button__forward">{{ $t('global.CONTINUE') }}<i class="material-icons-outlined">arrow_forward</i></button>
    </div>
  </div>
</template>


<script>
import { onMounted, ref } from 'vue';
import Input from '../../../components/Input.vue';
import Select from '../../../components/Inputs/Select.vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Disclaimer from './Disclaimer.vue';
import EmptyState from '@/components/EmptyState.vue';
import { useI18n } from 'vue-i18n';

export default {
  props: {
    data: {
      type: Object,
      required: true
    },
    creationOptions: {
      type: Object,
      required: true
    }
  },
  components: {
    Input,
    Select,
    Disclaimer,
    EmptyState
  },
  setup(props) {
    const { t } = useI18n();
    const percentaje = ref();
    const info = ref();
    const router = useRouter();
    const store = useStore();
    const submitting = ref(false);
    const certificateName = ref();
    const certificateDescription = ref();
    const durationInHours = ref();

    const getHourTypeName = (id) => {
      const hourType = props.creationOptions.certificateHourTypes.find(certificateHourType => certificateHourType.id === id);
      return hourType ? hourType.name : '';
    };

    const submit = async () => {
      if (submitting.value) return;
      try {
        submitting.value = true;
        if (props.data.status !== 'PUBLISHED') {
          await store.dispatch('courses_cfgfourthStep', info.value);
        }
        router.push('review');
      } catch (err) {
        console.log(err);
        store.dispatch('notifications_create', { text: t('certificateComponent.ERROR_SENDING_INFORMATION') });
      } finally {
        submitting.value = false;
      }
    };

    onMounted(() => {
      info.value = props.data
      if(!info.value.finalPractice) info.value.needFinalPractice = false;

      // preselect first hour type if not selected
      if (!info.value.certificateHourTypeId) {
        info.value.certificateHourTypeId = props.creationOptions.certificateHourTypes[0].id
      }

    })

    return {
      percentaje,
      info,
      submitting,
      router,
      certificateName,
      certificateDescription,
      durationInHours,
      getHourTypeName,
      submit
    };
  }
};
</script>


<style lang="scss" scoped>
@import '@/styles/main.scss';

.buttons{
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  .button{
      text-align: center;
      width: fit-content;
      display: flex;
      align-items: center;
      gap: 12px;
      border-radius: 999px;
      padding: 12px 18px;
      &__forward{
        @extend .button;
        color: #fff;
        background-color: $green;
      }
      &__backward{
        @extend .button;
        border: 1px solid $green;
        color: $green;
        background-color: inherit;
      }
      &:disabled{
        cursor: default !important;
        opacity: .6 !important;
      }
  }
}

.certificate{
  display: flex;
  flex-direction: column;
  max-width: 370px;
  width: 100%;
  margin: 0 auto;
  gap: 18px;
  align-items: center;
  &__title{
    font-size: 24px;
    font-weight: 800;
    text-align: center;
    align-self: center;
  }
  &__step{
    font-size: 14px;
    font-weight: 800;
    text-align: center;
    align-self: center;
  }
  &__step-data{
    font-size: 20px;
    font-weight: 600;
    text-align: center;
    align-self: center;
  }
  &__conditions{
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 18px;
    gap: 16px;
    &-title{
      font-size: 13px;
      font-weight: 700;
      text-align: center;
    }
    &-check{
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      font-size: 13px;
      font-weight: 400;
      text-align: center;
    }
    &-percentage{
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      max-width: 164px;
      margin: 0 auto;
    }
  }
  &__fileTemplate{
    width: 100%;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    &-title{
      font-size: 13px;
      font-weight: 700;
    }
  }
  &__fileUploaded{
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 26px 28px;
    background-color: #F6F6F6;
    width: 100%;
    &_data{
      display: flex;
      align-items: flex-start;
      gap: 12px;
      .material-icons-outlined:first-child{
        font-size: 28px;
      }
    }
    &_file{
      display: flex;
      flex-direction: column;
      gap: 6px;
      a{
        cursor: pointer;
        text-decoration: none;
        font-size: 16px;
        font-weight: 600;
        color: $green;
      }
      font-size: 16px;
      font-weight: 400;
    }
    .material-icons-outlined:last-child{
      cursor: pointer;
    }
  }
}
</style>
<style>
.certificate__conditions .dropdown input, .certificate__conditions .dropdown label {
  text-transform: capitalize;
}
</style>